import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { amplitudeContextSelector } from '@/features/amplitude/store/amplitude';
import { Provider } from '@/features/game-context/types';
import {
  getErrorType,
  PROVIDER_ERROR_MESSAGES,
} from '@/features/game-window-integration/helpers/error-mapping';
import {
  ProviderLoggerActionNames,
  ProviderLoggerMessages,
} from '@/features/game-window-integration/types';
import { ddNotifyAwagerFundsClicked } from '@/features/logging/action-logger';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';
import { processPayload } from '@/utils/process-error-payload';

export const useAwagerHandlers = <T extends Record<string, unknown>>(): {
  handleGameReady: () => void;
  handleSpinInProgress: (data: T) => void;
  handleNotImplemented: () => void;
  handleShowDeposit: (data: T) => void;
  handleCloseGame: () => void;
  handleSoundOn: () => void;
  handleSoundOff: () => void;
  getGameProviderFrame: () => Window | null | undefined;
  handleError: (data: T) => void;
  handleGameSwitch: (data: T) => void;
} => {
  const getGameProviderFrame = useCallback(
    () =>
      document.querySelector<HTMLIFrameElement>('#game-client')?.contentWindow,
    [],
  );

  const { gameSwitchEnabled } = useSelector(amplitudeContextSelector);

  const handleGameReady = useCallback(() => {
    setTimeout(() => {
      WrapperProxy.setGameLoaded();
    }, 1000);
  }, []);

  const handleSpinInProgress = useCallback((data: T) => {
    const { type } = data ?? {};

    if (!type) return;
    WrapperProxy.setSpinInProgress(type === 'gameRoundStart');
  }, []);

  const handleShowDeposit = useCallback((data: T) => {
    ddNotifyAwagerFundsClicked(data);
    WrapperProxy.showFunds();
  }, []);

  const handleCloseGame = useCallback(() => {
    WrapperProxy.setGameLauncherClosed();
  }, []);

  const handleSoundOn = useCallback(() => {
    WrapperProxy.setAudio(true);
  }, []);

  const handleSoundOff = useCallback(() => {
    WrapperProxy.setAudio(false);
  }, []);

  const handleNotImplemented = useCallback(() => {
    Logger.rumAction({
      name: ProviderLoggerMessages.NOT_IMPLEMENTED,
      useCase: DdEventCategory.ProviderIntegration,
      eventType: DdEventType.Info,
      context: {
        provider: Provider.AWAGER,
      },
    });
  }, []);

  const handleError = useCallback((data: T) => {
    const errorMeesageCode = PROVIDER_ERROR_MESSAGES[Provider.AWAGER][
      data?.category as string
    ]
      ? (data?.category as string)
      : (data.type as string);

    const errorType = getErrorType(
      data.type as string,
      Provider.AWAGER,
      false,
      data,
    );

    WrapperProxy.setProviderError({
      display: true,
      errorCode: data.type as string,
      errorMessage: getErrorType(errorMeesageCode, Provider.AWAGER, true),
      errorType,
    });
  }, []);

  const handleGameSwitch = useCallback(
    (data: T) => {
      Logger.rumAction({
        name: ProviderLoggerActionNames.GAME_SWITCH,
        useCase: DdEventCategory.ProviderIntegration,
        description: ProviderLoggerMessages.GAME_SWITCH,
        eventType: DdEventType.Info,
        context: {
          provider: Provider.AWAGER,
          source: data.source,
          rgsCode: data.rgsCode,
          ts: data.ts,
          type: data.type,
          value: data.value,
          stringifiedPayload: processPayload(data),
        },
      });

      WrapperProxy.setGameSwitch(
        {
          rgsCode: data.rgsCode as string,
          provider: Provider.AWAGER,
        },
        gameSwitchEnabled ?? false,
      );
    },
    [gameSwitchEnabled],
  );

  return {
    handleGameReady,
    handleSpinInProgress,
    handleNotImplemented,
    handleShowDeposit,
    handleCloseGame,
    handleError,
    getGameProviderFrame,
    handleSoundOn,
    handleSoundOff,
    handleGameSwitch,
  };
};
